import { setDoc, doc } from 'firebase/firestore';
import {
  sendEmailVerification,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { ref } from 'firebase/storage';
import { UploadFileToStorage } from '../utils/firebase/helper';
import {
  FIRESTORE_DOCTOR_TEMPLATE,
  FIRESTORE_PATIENT_TEMPLATE,
  FIRESTORE_TEACHER_TEMPLATE,
  FIRESTORE_STUDENT_TEMPLATE,
  FIRESTORE_SCHOOLADMIN_TEMPLATE,
  FIRESTORE_ORGANIZATIONADMIN_TEMPLATE,
} from '../constants/DataTemplates';
import { safeJsonBtoa } from '../utils/encodingUtils';
import { APP_URL } from '../constants/AppUrl';
import { fetchPreRegisteredUsers } from '../api/students';
import {
  FirebaseAuth,
  FirebaseStorage,
  FirestoreDB,
} from '../utils/firebase/firebase';

const typeOfUsers = {
  TEACHER: 'teachersList',
  STUDENT: 'studentsList',
  SCHOOLADMIN: 'schoolAdminsList',
  ORGANIZATIONADMIN: 'organizationAdminsList',
};

export const registerWithGoogle = async (data, infoParams, loginFn) => {
  try {
    if (infoParams.type === 'PATIENT') {
      // Only patients have this documents mapped to their ID. Doctors will only read based on patient id.
      await setDoc(doc(FirestoreDB, 'videoLogs', data.uid), {
        videos: [],
      });
      await setDoc(doc(FirestoreDB, 'patientHistory', data.uid), {
        history: [],
      });
    }

    // Add document to firestore collection
    const collectionName =
      infoParams.type === 'DOCTOR' ? 'doctorsList' : 'patientsList';

    const documentParams =
      infoParams.type === 'DOCTOR'
        ? {
            registrationID: infoParams.regID,
            fullname: data.displayName || '',
            email: data.email,
            photoURL: data.photoURL || '',
            ...FIRESTORE_DOCTOR_TEMPLATE,
          }
        : {
            doctorRegistrationID: infoParams.regID,
            fullname: data.displayName || '',
            email: data.email,
            photoURL: data.photoURL || '',
            ...FIRESTORE_PATIENT_TEMPLATE,
          };

    if (infoParams.type === 'DOCTOR') {
      await setDoc(doc(FirestoreDB, collectionName, data.uid), {
        profile: safeJsonBtoa(documentParams),
        isEncrypted: false,
      });
    }

    if (infoParams.type === 'PATIENT') {
      return {
        status: true,
        data: {
          uid: data.uid,
          fullname: data.displayName,
          email: data.email,
          photoURL: data.photoURL || '',
        },
      };
    }
    if (loginFn) await loginFn({ ...data });
    return { status: true, data: { uid: data?.uid, userEmail: data?.email } };
  } catch (err) {
    return { status: false, data: err };
  }
};

export const registerWithEmailPassword = async (
  email,
  password,
  fullname,
  parentName,
  profilePictureFile,
  infoParams
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      FirebaseAuth,
      email,
      password
    );
    const user = userCredential.user;
    const actionCodeSettings = {
      url:
        window.location.hostname === 'localhost'
          ? APP_URL.LOCAL
          : window.location.hostname === 'mindly-dev.web.app'
          ? APP_URL.DEV
          : APP_URL.PROD,
    };
    await sendEmailVerification(user, actionCodeSettings);
    let profilePictureUrl = '';
    if (profilePictureFile) {
      try {
        const blob = new Blob([profilePictureFile], {
          type: profilePictureFile.type,
        });
        const profileStorageRef = ref(
          FirebaseStorage,
          `profiles/${user?.uid}/${profilePictureFile.name}`
        );
        const uploadResult = await UploadFileToStorage(profileStorageRef, blob);
        if (uploadResult?.status) {
          profilePictureUrl = uploadResult?.data;
        }
      } catch (uploadError) {
        console.error('Error uploading profile picture:', uploadError);
      }
    }
    await updateProfile(user, {
      displayName: fullname,
      photoURL: profilePictureUrl,
    });

    if (infoParams.type === 'PATIENT') {
      try {
        const currentUser = FirebaseAuth?.currentUser;
        await Promise.all([
          setDoc(doc(FirestoreDB, 'videoLogs', currentUser.uid), {
            videos: [],
          }),
          setDoc(doc(FirestoreDB, 'patientHistory', currentUser.uid), {
            history: [],
          }),
          setDoc(doc(FirestoreDB, 'historicalData', currentUser.uid), {
            documents: [],
          }),
        ]);
      } catch (patientDocError) {
        console.error('Error creating patient documents:', patientDocError);
        throw new Error('Failed to create patient documents');
      }
    }
    const collectionName =
      infoParams.type === 'DOCTOR' ? 'doctorsList' : 'patientsList';
    const documentParams =
      infoParams.type === 'DOCTOR'
        ? {
            registrationID: infoParams.regID,
            fullname: fullname,
            email: user?.email,
            photoURL: profilePictureUrl || '',
            ...FIRESTORE_DOCTOR_TEMPLATE,
          }
        : {
            doctorRegistrationID: infoParams.regID,
            fullname: fullname,
            email: user?.email,
            parentName: parentName,
            photoURL: profilePictureUrl || '',
            ...FIRESTORE_PATIENT_TEMPLATE,
          };

    try {
      const currentUser = FirebaseAuth?.currentUser;
      await setDoc(doc(FirestoreDB, collectionName, currentUser.uid), {
        profile: safeJsonBtoa(documentParams),
        isEncrypted: infoParams.type === 'DOCTOR' ? false : true,
        isPoliciesAccepted: true,
      });
    } catch (firestoreError) {
      console.error('Error adding user document to Firestore:', firestoreError);
      throw new Error('Failed to save user document to Firestore');
    }

    if (infoParams.type === 'PATIENT') {
      return {
        status: true,
        data: {
          uid: user?.uid,
          fullname: fullname,
          parentName: parentName,
          email: user?.email,
          photoURL: profilePictureUrl || '',
        },
      };
    }
    return {
      status: true,
      data: { uid: user.uid, userEmail: user?.email },
    };
  } catch (error) {
    console.error('Error during registration:', error);
    return { status: false, error: error.message || error };
  }
};

export const registerWithEmailPasswordForTeacherAndStudent = async (
  data,
  uploadProfilePicFn
) => {
  try {
    const {
      email,
      password,
      profilePictureFile,
      status = 'active',
      inviteType,
      ...rest
    } = data;
    const preRegisteredUserResponse = await fetchPreRegisteredUsers(email);

    let additionalData = {};
    // fetchPreRegisteredUsers returns an array, get the first user if available
    let preRegisteredUser =
      Array.isArray(preRegisteredUserResponse) &&
      preRegisteredUserResponse.length > 0
        ? preRegisteredUserResponse[0]
        : preRegisteredUserResponse;

    if (preRegisteredUser && typeof preRegisteredUser === 'object') {
      // Extract user data if wrapped in response object
      preRegisteredUser = preRegisteredUser.user || preRegisteredUser;

      // Handle case where data has preregister data at key "0" and student template data in rest
      // Merge preregister data (key "0") with student template data
      // NOTE: This ONLY runs if key "0" exists - existing students without "0" key will remain unchanged
      if (
        preRegisteredUser['0'] &&
        typeof preRegisteredUser['0'] === 'object'
      ) {
        const preregisterData = preRegisteredUser['0'];
        const studentTemplateData = { ...preRegisteredUser };
        delete studentTemplateData['0'];

        // Start with preregister data as base
        additionalData = { ...preregisterData };

        // Merge with student template data, preserving important preregister fields
        Object.keys(studentTemplateData).forEach((key) => {
          const templateValue = studentTemplateData[key];
          const preregisterValue = additionalData[key];

          // Handle contactNo - use preregister data if template is empty
          if (key === 'contactNo') {
            if (
              !templateValue ||
              (!templateValue.areaCode && !templateValue.number)
            ) {
              if (
                preregisterValue &&
                (preregisterValue.areaCode || preregisterValue.number)
              ) {
                additionalData[key] = preregisterValue;
              }
            } else {
              additionalData[key] = {
                areaCode:
                  templateValue.areaCode || preregisterValue?.areaCode || '',
                number: templateValue.number || preregisterValue?.number || '',
              };
            }
          }
          // Handle id - skip this field, it will be set to user.uid later in profileData
          // The id field is explicitly set to user.uid at line 391, so we don't merge it here
          else if (key === 'id') {
            // Skip - id will be set to real userId (user.uid) in profileData construction
            // Don't merge preregister id here as it gets overwritten anyway
          }
          // Handle studentId - prefer preregister if available
          else if (key === 'studentId') {
            additionalData[key] = preregisterValue || templateValue || '';
          }
          // For other fields, prefer template value if not empty, otherwise use preregister
          else if (
            templateValue !== undefined &&
            templateValue !== null &&
            templateValue !== ''
          ) {
            if (Array.isArray(templateValue) && templateValue.length > 0) {
              additionalData[key] = templateValue;
            } else if (
              typeof templateValue === 'object' &&
              Object.keys(templateValue).length > 0
            ) {
              additionalData[key] = templateValue;
            } else if (typeof templateValue !== 'object') {
              additionalData[key] = templateValue;
            } else if (preregisterValue) {
              additionalData[key] = preregisterValue;
            }
          } else if (
            preregisterValue !== undefined &&
            preregisterValue !== null &&
            preregisterValue !== ''
          ) {
            additionalData[key] = preregisterValue;
          }
        });

        // Clean up: Remove migration-only fields to match existing user profile format
        delete additionalData.preregisterId;
        delete additionalData.userId;
        delete additionalData.authId;
      } else {
        // No key "0" found - this is normal student data, use as-is without merging
        // Existing students will follow this path and remain unchanged
        additionalData = preRegisteredUser;
      }
    } else {
      console.log(
        ':small_blue_diamond: No pre-registered user found. Proceeding with regular registration.'
      );
    }

    const userCredential = await createUserWithEmailAndPassword(
      FirebaseAuth,
      email,
      password
    );
    console.log('uerCredential at auth:', userCredential);
    const user = userCredential?.user;
    console.log('user at auth after create:', user);

    // Sign in the user to ensure they are authenticated for Firestore operations
    await signInWithEmailAndPassword(FirebaseAuth, email, password);

    const actionCodeSettings = {
      url:
        window.location.hostname === 'localhost'
          ? APP_URL.LOCAL
          : window.location.hostname === 'mindly-dev.web.app'
          ? APP_URL.DEV
          : APP_URL.PROD,
    };
    console.log('actionCodeSettings:', actionCodeSettings);
    await sendEmailVerification(user, actionCodeSettings);

    let photoURL = null;
    if (profilePictureFile && uploadProfilePicFn) {
      photoURL = await uploadProfilePicFn(user, profilePictureFile);
    }

    const finalFullName = additionalData.fullname || rest.fullname || '';
    const finalEmail = additionalData.email || email;

    await updateProfile(user, {
      displayName: finalFullName,
      photoURL,
    });

    if (inviteType.includes('STUDENT')) {
      try {
        const currentUser = FirebaseAuth?.currentUser;

        await setDoc(doc(FirestoreDB, 'videoLogs', currentUser?.uid), {
          videos: [],
        });
        await setDoc(doc(FirestoreDB, 'patientHistory', currentUser.uid), {
          history: [],
        });
        await setDoc(doc(FirestoreDB, 'historicalData', currentUser.uid), {
          documents: [],
        });
      } catch (patientDocError) {
        console.error('Error creating patient documents:', patientDocError);
        console.error('Error code:', patientDocError.code);
        console.error('Error message:', patientDocError.message);
        throw new Error('Failed to create patient documents');
      }
    }

    for (const role of inviteType) {
      console.log(`Processing role: ${role} for user: ${user.uid}`);
      console.log(`Collection: ${typeOfUsers[role]}`);
      const profileData = {
        email: finalEmail,
        photoURL,
        fullname: finalFullName,
        ...additionalData,
        ...rest,
        ...(role === 'TEACHER' && {
          ...FIRESTORE_TEACHER_TEMPLATE,
          fullname: data.fullname,
          photoURL: photoURL || '',
        }),
        ...(role === 'STUDENT' && {
          ...FIRESTORE_STUDENT_TEMPLATE,
          fullname: finalFullName,
          email: finalEmail,
          parentName: data.parentName,
          dob: additionalData?.dob,
          contactNo: {
            areaCode: additionalData?.contactNo?.areaCode,
            number: additionalData?.contactNo?.number,
          },
          gender: additionalData?.gender,
          photoURL: photoURL || '',
          id: user.uid,
          studentId:
            additionalData?.studentId ||
            additionalData?.admissionNumber ||
            data?.studentId ||
            data?.admissionNumber ||
            '',
        }),
        ...(role === 'SCHOOLADMIN' && {
          ...FIRESTORE_SCHOOLADMIN_TEMPLATE,
          fullname: data.fullname,
          photoURL: photoURL || '',
        }),
        ...(role === 'ORGANIZATIONADMIN' && {
          ...FIRESTORE_ORGANIZATIONADMIN_TEMPLATE,
        }),
      };

      const docData = {
        profile: safeJsonBtoa(profileData),
        schoolId: rest.schoolId || additionalData.schoolId || '',
        teacherIds: rest.teacherIds || additionalData.teacherIds || [],
        organizationId:
          rest.organizationId || additionalData.organizationId || '',
        isEncrypted: false,
        isPoliciesAccepted: true,
        status,
        ...(rest.isIndividualUser && { isIndividualUser: true }),
        ...(rest.isParentCareGiverAccount && { isParentCareGiverAccount: true }),
        ...(rest.isSelfAdvocate && { isSelfAdvocate: true }),
      };

      try {
        const currentUser = FirebaseAuth?.currentUser;

        await setDoc(
          doc(FirestoreDB, typeOfUsers[role], currentUser.uid),
          docData,
          {
            merge: true,
          }
        );
      } catch (collectionError) {
        console.error(
          `Error writing to collection ${typeOfUsers[role]}:`,
          collectionError
        );
        console.error(`Error code: ${collectionError.code}`);
        console.error(`Error message: ${collectionError.message}`);
        console.error(`Full error object:`, collectionError);
        throw collectionError;
      }
    }
    // The document will be deleted after migration completes during email verification
    // Migration happens in AuthProvider.js when user clicks email verification link
    // This preserves all pre-registered data (videos, attachments, goals, treatments) for migration

    // Store preRegisteredUser data for migration during email verification
    // This is already handled in the Invite page component, but keeping reference here for clarity
    return { status: true, data: { uid: user?.uid, email: user.email } };
  } catch (error) {
    console.error('Error during registration:', error);
    return { status: false, error: error.message };
  }
};

export const registerNewPatient = async (
  patientInfo,
  doctorId,
  loginFn,
  user
) => {
  try {
    // Only patients have this documents mapped to their ID. Doctors will only read based on patient id.
    await setDoc(doc(FirestoreDB, 'videoLogs', patientInfo.uid), {
      videos: [],
    });
    await setDoc(doc(FirestoreDB, 'patientHistory', patientInfo.uid), {
      history: [],
    });

    // Add document to firestore collection
    const collectionName = 'patientsList';
    const documentParams = {
      fullname: patientInfo?.fullname,
      email: patientInfo.email,
      photoURL: patientInfo.photoURL || '',
      parentName: patientInfo.parentName || '',
      ...FIRESTORE_PATIENT_TEMPLATE,
    };
    await setDoc(doc(FirestoreDB, collectionName, patientInfo.uid), {
      profile: safeJsonBtoa(documentParams),
      isEncrypted: false,
      isPoliciesAccepted: true,
      doctorIDs: [doctorId],
    });

    if (loginFn && user) await loginFn(user);
    return { status: true, data: null };
  } catch (err) {
    return { status: false, data: err };
  }
};
